import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, 
  PlusCircle, 
  ChevronRight, 
  Calendar, 
  Trash2,
  Search,
  X,
  ArrowLeft,
  Clock,
  Filter,
  LayoutGrid
} from 'lucide-react';
import { useConversations } from '../../hooks/useConversations';
import { archiveConversation } from '../../utils/db-messaging';
import StatsPanel from './desktopStatsPanel';
import { classifyConversation, getConversationFilters, sortConversationsByPriority } from '../../utils/conversationClassifier';

// Import Brazilian detector and mappings
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { chatHistoryPanelPtBR } from '../mappings/chatHistoryPanel.pt-br';

// ConversationItem Component - Optimized with stable props checking
const ConversationItem = React.memo(({ 
  conversation, 
  isActive, 
  isDeleting, 
  deleteTarget, 
  onSelect, 
  onDelete,
  formatDate 
}) => {
  const handleSelectClick = useCallback(() => {
    onSelect(conversation);
  }, [conversation, onSelect]); // Keep conversation object for onSelect to work properly

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    onDelete(e, conversation.id);
  }, [conversation.id, onDelete]);
  
  // Memoize category style calculation to prevent recalculation
  const categoryStyle = useMemo(() => {
    return classifyConversation(
      conversation.metadata?.title,
      conversation.lastMessage
    );
  }, [conversation.metadata?.title, conversation.lastMessage]); // Recalculate if title or last message changes
  
  const { icon: CategoryIcon, color, bgColor, label } = categoryStyle;

  // Memoize the formatted date to prevent recalculation
  const formattedDate = useMemo(() => {
    return formatDate(conversation.metadata?.updatedAt);
  }, [conversation.metadata?.updatedAt, formatDate]);

  return (
    <motion.button
      onClick={handleSelectClick}
      className={`w-full text-left py-2 px-3 rounded-lg transition-all duration-200 group relative ${
        isActive
          ? 'bg-emerald-500/20 text-emerald-100'
          : 'hover:bg-white/5 text-white/70'
      }`}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      layout
    >
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${bgColor}`}>
          <CategoryIcon className={`w-3.5 h-3.5 ${color}`} />
        </div>
        
        <span className="truncate text-sm font-medium flex-1">
          {conversation.metadata?.title || "Untitled Conversation"}
        </span>
        
        {label && (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${bgColor} ${color}`}>
            {label}
          </span>
        )}
      </div>
      
      <div className="flex items-center justify-between mt-1 text-xs">
        <div className="flex items-center text-white/50 gap-1">
          <Calendar className="w-3 h-3" />
          <span>{formattedDate}</span>
        </div>
        
        <div className="text-white/50">
          {conversation.metadata?.messageCount || 0} msgs
        </div>
      </div>
      
      {/* Active conversation indicator - Only render when active */}
      {isActive && (
        <motion.div 
          className="absolute inset-0 rounded-lg border border-emerald-400/30 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="absolute inset-0 translate-x-full"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent)",
                backgroundSize: "200% 100%"
              }}
              animate={{ translateX: ['-100%', '100%'] }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "linear" 
              }}
            />
          </motion.div>
        </motion.div>
      )}
      
      {/* Delete button with improved hover effect */}
      <button
        onClick={handleDeleteClick}
        className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 ${
          isDeleting && deleteTarget === conversation.id
            ? 'bg-red-500/20 text-red-400 opacity-100 scale-110'
            : 'hover:bg-slate-700/50 text-white/50 hover:text-white/80'
        }`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </motion.button>
  );
}, (prevProps, nextProps) => {
  // If this is the active conversation, always allow updates to keep it current
  if (nextProps.isActive || prevProps.isActive) {
    return false; // Always re-render active conversations
  }
  
  // For inactive conversations, only re-render if essential props change
  return (
    prevProps.conversation.id === nextProps.conversation.id &&
    prevProps.conversation.metadata?.title === nextProps.conversation.metadata?.title &&
    prevProps.isDeleting === nextProps.isDeleting &&
    prevProps.deleteTarget === nextProps.deleteTarget
  );
});

ConversationItem.displayName = 'ConversationItem';

// SectionTitle - Consistent section headers
const SectionTitle = React.memo(({ icon: Icon, title, count }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 text-white/50 text-xs font-medium uppercase tracking-wider mb-1">
    <Icon className="w-3.5 h-3.5" />
    <span>{title}</span>
    {count !== undefined && (
      <span className="ml-auto bg-white/10 rounded-full px-1.5 py-0.5 text-[10px]">{count}</span>
    )}
  </div>
));

SectionTitle.displayName = 'SectionTitle';

// SearchInput - Dedicated search component
const SearchInput = React.memo(({ 
  searchQuery, 
  setSearchQuery, 
  searchInputRef 
}) => {
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, [searchInputRef, setSearchQuery]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/50" />
      <input
        ref={searchInputRef}
        type="text"
        placeholder="Search conversations..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full py-2 px-3 pl-10 pr-8 bg-slate-800/50 border border-white/10 rounded-lg text-white placeholder-white/40 
          focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/20 transition-colors duration-200"
      />
      {searchQuery && (
        <button
          onClick={clearSearch}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 p-1 rounded-full hover:bg-white/5 transition-colors"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

// FilterTabs - Filter options for conversations with updated categories
const FilterTabs = React.memo(({ 
  activeFilter, 
  setActiveFilter,
  conversations
}) => {
  const filters = useMemo(() => {
    return getConversationFilters(conversations);
  }, [conversations]);

  return (
    <div className="flex gap-1 mb-3 overflow-x-auto scrollbar-none pb-1">
      {filters.map(filter => {
        const Icon = filter.icon;
        const isActive = activeFilter === filter.id;
        
        return (
          <motion.button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            className={`px-2 py-1 rounded-md text-xs flex items-center gap-1.5 whitespace-nowrap ${
              isActive 
                ? 'bg-emerald-500/20 text-emerald-300' 
                : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon className="w-3 h-3" />
            <span>{filter.label}</span>
            {filter.count > 0 && (
              <span className={`rounded-full px-1 text-[10px] ${
                isActive ? 'bg-emerald-500/30' : 'bg-white/10'
              }`}>
                {filter.count}
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
});

FilterTabs.displayName = 'FilterTabs';

// Empty state component
const EmptyState = React.memo(({ 
  searchQuery, 
  activeFilter,
  setSearchQuery, 
  setActiveFilter,
  handleNewChat
}) => {
  if (searchQuery) {
    return (
      <div className="p-8 text-center flex flex-col items-center">
        <Search className="w-8 h-8 text-white/20 mb-3" />
        <p className="text-white/50 text-sm mb-2">No conversations match your search</p>
        <motion.button
          onClick={() => setSearchQuery('')}
          className="text-emerald-400 text-xs hover:text-emerald-300 px-2 py-1 hover:bg-emerald-500/10 rounded-md transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Clear search
        </motion.button>
      </div>
    );
  }
  
  if (activeFilter !== 'all') {
    return (
      <div className="p-8 text-center flex flex-col items-center">
        <Filter className="w-8 h-8 text-white/20 mb-3" />
        <p className="text-white/50 text-sm mb-2">No conversations in this filter</p>
        <motion.button
          onClick={() => setActiveFilter('all')}
          className="text-emerald-400 text-xs hover:text-emerald-300 px-2 py-1 hover:bg-emerald-500/10 rounded-md transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Show all conversations
        </motion.button>
      </div>
    );
  }
  
  return (
    <div className="p-8 text-center flex flex-col items-center">
      <MessageCircle className="w-8 h-8 text-white/20 mb-3" />
      <p className="text-white/50 text-sm mb-2">No conversations yet</p>
      <motion.button
        onClick={handleNewChat}
        className="mt-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-md transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Start a conversation
      </motion.button>
    </div>
  );
});

EmptyState.displayName = 'EmptyState';

// Loading indicator component
const LoadingIndicator = React.memo(() => (
  <div className="flex justify-center items-center h-20">
    <div className="flex gap-1">
      {[0, 0.1, 0.2].map((delay, index) => (
        <motion.div 
          key={index}
          className="w-2 h-2 bg-emerald-400 rounded-full"
          animate={{
            y: [0, -5, 0],
            opacity: [1, 0.4, 1]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut",
            delay
          }}
        />
      ))}
    </div>
  </div>
));

LoadingIndicator.displayName = 'LoadingIndicator';

// Main Component
const ChatHistoryPanel = ({ 
  userId, 
  activeConversationId, 
  onChatSelect, 
  onNewChat 
}) => {
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Simple translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return chatHistoryPanelPtBR[text] || text;
  };

  // States
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  
  // References
  const searchInputRef = useRef(null);
  
  // Get conversations using the hook
  const { 
    conversations, 
    loading, 
    error,
    selectConversation
  } = useConversations(userId);
  
  // Focus search input when search is shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);
  
  // Stable callback references to prevent child re-renders
  const stableCallbacks = useMemo(() => ({
    toggleSearch: () => {
      setShowSearch(prev => !prev);
      if (showSearch) {
        setSearchQuery('');
      }
    },
    cancelDelete: () => {
      setIsDeleting(false);
      setDeleteTarget(null);
    },
    toggleCollapsed: () => {
      setIsCollapsed(prev => !prev);
    },
    handleNewChat: () => {
      if (isCollapsed) {
        setIsCollapsed(false);
      }
      onNewChat();
    }
  }), [showSearch, isCollapsed, onNewChat]);
  
  // Memoize format date function with stable reference
  const formatDate = useCallback((date) => {
    if (!date) return 'Unknown';
    
    const now = new Date();
    const conversationDate = new Date(date);
    
    // Check if the date is today
    if (now.toDateString() === conversationDate.toDateString()) {
      return `Today at ${conversationDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Check if the date is yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (yesterday.toDateString() === conversationDate.toDateString()) {
      return `Yesterday at ${conversationDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // If it's within the last week, return the day name
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    if (conversationDate > oneWeekAgo) {
      return conversationDate.toLocaleDateString([], { weekday: 'long' });
    }
    
    // Otherwise return the full date
    return conversationDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }, []);
  
  
  // Optimized filtered conversations - more efficient filtering
  const filteredConversations = useMemo(() => {
    if (!conversations || conversations.length === 0) return [];
    
    // Apply active filter
    let filtered = [...conversations];
    
    if (activeFilter !== 'all') {
      filtered = filtered.filter(conv => {
        const classification = classifyConversation(
          conv.metadata?.title,
          conv.lastMessage
        );
        return classification.category === activeFilter;
      });
    }
    
    // Then apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => {
        const titleMatch = (conv.metadata?.title || '').toLowerCase().includes(query);
        const messageMatch = (conv.lastMessage || '').toLowerCase().includes(query);
        return titleMatch || messageMatch;
      });
    }
    
    // Sort by priority
    return sortConversationsByPriority(filtered);
  }, [conversations, searchQuery, activeFilter]); // Simplified dependencies
  
  // Group conversations by date section - simplified dependency tracking
  const groupedConversations = useMemo(() => {
    const groups = {};
    
    filteredConversations.forEach(conv => {
      const date = conv.metadata?.updatedAt;
      
      // Skip if no date available
      if (!date) return;
      
      const dateObj = new Date(date);
      const now = new Date();
      
      let groupKey;
      
      // Today
      if (dateObj.toDateString() === now.toDateString()) {
        groupKey = 'Today';
      }
      // Yesterday
      else {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        
        if (dateObj.toDateString() === yesterday.toDateString()) {
          groupKey = 'Yesterday';
        }
        // This week
        else {
          const thisWeekStart = new Date(now);
          thisWeekStart.setDate(now.getDate() - now.getDay());
          
          if (dateObj >= thisWeekStart) {
            groupKey = 'This Week';
          }
          // This month
          else {
            const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            
            if (dateObj >= thisMonthStart) {
              groupKey = 'This Month';
            }
            // Earlier
            else {
              groupKey = 'Earlier';
            }
          }
        }
      }
      
      // Initialize group if it doesn't exist
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      
      groups[groupKey].push(conv);
    });
    
    // Sort conversations within each group
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => {
        const dateA = a.metadata?.updatedAt ? new Date(a.metadata.updatedAt) : new Date(0);
        const dateB = b.metadata?.updatedAt ? new Date(b.metadata.updatedAt) : new Date(0);
        return dateB - dateA;
      });
    });
    
    return groups;
  }, [filteredConversations]); // Simplified dependency
  
  // Stable callback for delete with proper dependency tracking
  const handleDelete = useCallback(async (e, conversationId) => {
    e.stopPropagation();
    
    // If we're not in delete mode, enter it
    if (!isDeleting) {
      setIsDeleting(true);
      setDeleteTarget(conversationId);
      return;
    }
    
    // If we're in delete mode and this is the target, delete it
    if (deleteTarget === conversationId) {
      try {
        await archiveConversation(userId, conversationId);
        setIsDeleting(false);
        setDeleteTarget(null);
        
        // If the deleted conversation was active, create a new chat
        if (conversationId === activeConversationId) {
          onNewChat();
        }
      } catch (error) {
        console.error('Error deleting conversation:', error);
      }
    } else {
      // If we're in delete mode but targeting a different conversation
      setDeleteTarget(conversationId);
    }
  }, [isDeleting, deleteTarget, userId, activeConversationId, onNewChat]);
  
  // Stable callback for conversation selection
  const handleConversationSelect = useCallback((conversation) => {
    // Exit delete mode if active
    if (isDeleting) {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
    
    onChatSelect(conversation);
  }, [onChatSelect, isDeleting]);
  
  // Render collapsible side view
  const renderCollapsedView = () => (
    <div className="h-full flex flex-col items-center py-4">
      <button
        onClick={stableCallbacks.toggleCollapsed}
        className="p-2 rounded-full hover:bg-white/5 transition-colors mb-4"
        aria-label="Expand panel"
      >
        <ChevronRight className="w-5 h-5 text-emerald-400" />
      </button>
      
      <motion.button
        onClick={stableCallbacks.handleNewChat}
        className="p-3 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors mb-4"
        aria-label={t("New chat")}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <PlusCircle className="w-5 h-5 text-emerald-400" />
      </motion.button>
    </div>
  );
  
  // Render delete confirmation component
  const renderDeleteConfirmation = () => (
    <AnimatePresence>
      {isDeleting && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="p-4 border-t border-white/10 bg-slate-800/90 backdrop-blur-sm"
        >
          <div className="text-xs text-white/70 mb-2">
            Delete this conversation? This cannot be undone.
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={(e) => handleDelete(e, deleteTarget)}
              className="flex-1 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm font-medium transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Delete
            </motion.button>
            <motion.button
              onClick={stableCallbacks.cancelDelete}
              className="flex-1 py-1.5 bg-slate-700/50 hover:bg-slate-700 text-white/70 rounded text-sm transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
  
  // Render conversation groups with date sections
  const renderConversationGroups = () => {
    if (loading) {
      return <LoadingIndicator />;
    }
    
    if (error) {
      return (
        <div className="text-red-400 text-sm p-4 text-center">
          {error.message || "Error loading conversations"}
        </div>
      );
    }
    
    if (Object.keys(groupedConversations).length === 0) {
      return (
        <EmptyState
          searchQuery={searchQuery}
          activeFilter={activeFilter}
          setSearchQuery={setSearchQuery}
          setActiveFilter={setActiveFilter}
          handleNewChat={stableCallbacks.handleNewChat}
        />
      );
    }
    
    // Define the correct order for groups (most recent first)
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'Earlier'];
    
    // Sort the groups according to the defined order
    const sortedGroups = Object.entries(groupedConversations).sort((a, b) => {
      const indexA = groupOrder.indexOf(a[0]);
      const indexB = groupOrder.indexOf(b[0]);
      return indexA - indexB;
    });
    
    return (
      <>
        {sortedGroups.map(([groupName, convs]) => (
          <div key={groupName} className="mb-4">
            <SectionTitle 
              icon={groupName === 'Today' ? Clock : Calendar} 
              title={groupName} 
              count={convs.length} 
            />
            
            <ul className="space-y-1">
              {convs.map(conversation => (
                <li key={conversation.id}>
                  <ConversationItem
                    conversation={conversation}
                    isActive={conversation.id === activeConversationId}
                    isDeleting={isDeleting}
                    deleteTarget={deleteTarget}
                    onSelect={handleConversationSelect}
                    onDelete={handleDelete}
                    formatDate={formatDate}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  };
  
  // Render expanded view with all features
  const renderExpandedView = () => (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h2 className="text-lg font-medium text-white flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-400" />
          {t("Chat history")}
        </h2>
        <button
          onClick={stableCallbacks.toggleCollapsed}
          className="p-1 rounded hover:bg-white/5 transition-colors"
          aria-label="Collapse panel"
        >
          <ArrowLeft className="w-4 h-4 text-white/70" />
        </button>
      </div>
      
      <div className="p-4">
        <StatsPanel userId={userId} />
      </div>
      
      <div className="px-4 mb-2 space-y-3">
        <motion.button
          onClick={stableCallbacks.handleNewChat}
          className="w-full py-2 px-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <PlusCircle className="w-4 h-4" />
          <span>{t("New chat")}</span>
        </motion.button>
        
        {/* Show filter tabs if there are conversations */}
        {conversations && conversations.length > 0 && (
          <FilterTabs 
            activeFilter={activeFilter} 
            setActiveFilter={setActiveFilter}
            conversations={conversations}
          />
        )}
        
        <div className="flex gap-2">
          <button
            onClick={stableCallbacks.toggleSearch}
            className={`p-2 rounded-lg transition-colors flex-1 flex items-center justify-center gap-2 text-sm ${
              showSearch 
                ? 'bg-emerald-500/20 text-emerald-300' 
                : 'bg-slate-800/50 text-white/70 hover:bg-slate-700/50'
            }`}
            aria-label={showSearch ? "Hide search" : "Show search"}
          >
            <Search className="w-4 h-4" />
            <span>{t("Search")}</span>
          </button>
        </div>
      </div>
      
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-4 mb-2 overflow-hidden"
          >
            <SearchInput 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchInputRef={searchInputRef}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-emerald-600/20 hover:scrollbar-thumb-emerald-500/40 scrollbar-track-transparent px-2 transition-colors">
        {renderConversationGroups()}
      </div>
      
      {renderDeleteConfirmation()}
    </div>
  );
  
  return (
    <div 
      className={`h-full border-r border-white/10 bg-slate-900/80 backdrop-blur-sm transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-72'
      } ml-[72px]`}
    >
      {isCollapsed ? renderCollapsedView() : renderExpandedView()}
    </div>
  );
};

export default React.memo(ChatHistoryPanel);